import { Dialog } from "@headlessui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormBox, ModalFoterBox } from "../../../components/box/Box";
import Button from "../../../components/button/Button";
import { TextField } from "../../../components/fields/Fields";
import FormController from "../../../components/fields/FormController";
import { Modal, ModalProp } from "../../../components/modal/Modal";
import { RadioButton } from "../../../components/radiobutton/RadioButton";
import { useInput } from "../../../hooks/useInput";
import { ButtonSettingItems, ReplyButton, replyButtonSchema } from "../../../model/ReplyButton";
import { ConstMessage } from "../../../config/const";

export type ReplyButtonModalProp = {
  replyButton?: ReplyButton;
  authorUid: string;
  onSubmit: (
    data: ReplyButton & ButtonSettingItems
  ) => Promise<void>;
}

export const ReplyButtonsModal = ({
  open,
  toggle,
  closeOnClickOutside,
  updateFlg,
  replyButton,
  authorUid,
  onSubmit,
}: ModalProp & ReplyButtonModalProp) => {
  const { control, handleSubmit, watch, reset, setValue, getValues } =
    useForm<ReplyButton & ButtonSettingItems>({
      resolver: yupResolver(replyButtonSchema),
      defaultValues: replyButton,
    });

  const selectNumgerItems = [
    {id: "1", title: "1"},
    {id: "2", title: "2"},
    {id: "3", title: "3"}
  ];

  const selectColor1Items = [
    {id: "blue1", title: "青", value: "0"},
    {id: "red1", title: "赤", value: "1"},
    {id: "green1", title: "緑", value: "2"},
  ];

  const selectColor2Items = [
    {id: "blue2", title: "青", value: "0"},
    {id: "red2", title: "赤", value: "1"},
    {id: "green2", title: "緑", value: "2"},
  ];

  const selectColor3Items = [
    {id: "blue3", title: "青", value: "0"},
    {id: "red3", title: "赤", value: "1"},
    {id: "green3", title: "緑", value: "2"},
  ];

  const selectNumber = useInput("3");
  const selectColor1 = useInput("0");
  const selectColor2 = useInput("1");
  const selectColor3 = useInput("2");

  useEffect(() => {
    setValue('id', replyButton?.id ?? '');
    setValue('name', replyButton?.name ?? '');
    setValue('buttonNumber', replyButton?.buttonNumber ?? 3);
    setValue('buttonName', replyButton?.buttonName ?? []);
    setValue('buttonName1', replyButton?.buttonName[0] ?? '');
    setValue('buttonName2', replyButton?.buttonName[1] ?? '');
    setValue('buttonName3', replyButton?.buttonName[2] ?? '');
    setValue('buttonColor', replyButton?.buttonColor.length !== 0
      ? replyButton?.buttonColor!
      : ['0', '1', '2']
    );
    setValue('type', replyButton?.type ?? '');
    setValue('createUser', authorUid);
    setValue('createAt', replyButton?.createAt ?? new Date());
    selectNumber.setValue(replyButton?.buttonNumber.toString());
    selectColor1.setValue(replyButton?.buttonColor[0] ?? '0');
    selectColor2.setValue(replyButton?.buttonColor[1] ?? '1');
    selectColor3.setValue(replyButton?.buttonColor[2] ?? '2');

    if(open) {
      // 重複チェック初期化
      sessionStorage.removeItem("dupCheck");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[replyButton, setValue, updateFlg, authorUid, open]);

  // ボタン数
  useEffect(() => {
    setValue('buttonNumber', selectNumber.value);
    const buttonColor = getValues('buttonColor');
    buttonColor[0] = selectColor1.value;
    buttonColor[1] = selectColor2.value;
    buttonColor[2] = selectColor3.value;
    setValue('buttonColor', buttonColor);
  },[getValues, selectColor1.value, selectColor2.value, selectColor3.value, selectNumber.value, setValue]);

  // ボタン1
  useEffect(() => {
    const buttonColor = getValues('buttonColor');
    buttonColor[0] = selectColor1.value;
    setValue('buttonColor', buttonColor);
  },[getValues, selectColor1.value, setValue]);

  // ボタン2
  useEffect(() => {
    const buttonColor = getValues('buttonColor');
    buttonColor[1] = selectColor2.value;
    setValue('buttonColor', buttonColor)
  },[getValues, selectColor2.value, setValue]);

  // ボタン3
  useEffect(() => {
    const buttonColor = getValues('buttonColor');
    buttonColor[2] = selectColor3.value;
    setValue('buttonColor', buttonColor)
  },[getValues, selectColor3.value, setValue]);

  useEffect(() => {
    if(!open) {
      reset();
      selectNumber.setValue(replyButton?.buttonNumber.toString());
      selectColor1.setValue(replyButton?.buttonColor[0] ?? '0');
      selectColor2.setValue(replyButton?.buttonColor[1] ?? '1');
      selectColor3.setValue(replyButton?.buttonColor[2] ?? '2');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open, replyButton?.buttonColor, replyButton?.buttonNumber, reset]);

  // ボタン設定名変更時
  useEffect(() => {
    // dupCheckをリセット(false)
    sessionStorage.setItem("dupCheck", "false");
    // 入力値更新
    setValue("name",watch("name"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[watch("name")]);

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className=" relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all sm:w-full max-w-screen-md ">
          <form
            className=""
            onSubmit={handleSubmit(onSubmit)}
          >
          <div className="px-2 pb-4 sm:p-4">
            <div className="text-center sm:mt-3">
              <Dialog.Title
                as="h3"
                className="text-left text-lg font-medium leading-6 text-gray-900"
              >
                返信ボタン作成
              </Dialog.Title>
              <div className="overflow-y-auto">
                <div className="px-2 text-left pb-8 h-[500px] ">
                  <FormBox>
                    <RadioButton
                      children={undefined}
                      name={"buttonNumber"}
                      label={"ボタン数"}
                      value={selectNumber}
                      radioButtonItem={selectNumgerItems}
                      vertical />
                  </FormBox>
                  <FormBox>
                    <Controller
                      name={"name"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type={"text"}
                          label={"設定名"}
                          required
                          error={fieldState.error || sessionStorage.getItem("dupCheck") === "true"}
                          helperText={fieldState.error?.message || (sessionStorage.getItem("dupCheck") === "true" ? ConstMessage.ERR_SAVE_REPLY_BUTTONS_NAME : "")}
                        />
                      )}
                    />
                  </FormBox>
                  <FormBox className="flex">
                    <FormController
                      control={control}
                      name={"buttonName1"}
                      label={"ボタン"}
                      type={"text"}
                      required
                    />
                    <RadioButton
                      className="h-20"
                      children={undefined}
                      name={"buttonColor1"}
                      value={selectColor1}
                      radioButtonItem={selectColor1Items}
                      vertical />
                  </FormBox>
                  {(selectNumber.value === "2" || selectNumber.value === "3") &&
                    (
                      <FormBox className="flex">
                        <FormController
                          control={control}
                          name={"buttonName2"}
                          label={"ボタン"}
                          type={"text"}
                          required
                        />
                        <RadioButton
                          className="h-20"
                          children={undefined}
                          name={"buttonColor2"}
                          value={selectColor2}
                          radioButtonItem={selectColor2Items}
                          vertical />
                      </FormBox>
                    )
                  }
                  {(selectNumber.value === "3") &&
                    (
                      <FormBox className="flex">
                        <FormController
                          control={control}
                          name={"buttonName3"}
                          label={"ボタン"}
                          type={"text"}
                          required
                        />
                        <RadioButton
                          className="h-20"
                          children={undefined}
                          name={"buttonColor3"}
                          value={selectColor3}
                          radioButtonItem={selectColor3Items}
                          vertical />
                      </FormBox>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
          <ModalFoterBox>
            <Button
              className="mx-2 mt-1 w-[132px]"
              outline
              onClick={toggle}
              children={"キャンセル"}
            />
            <Button
              className="mt-1 mr-2 w-[132px]"
              submit
              children={"登録"}
            />
          </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </Modal>
    </>
  );
}
